import React, { useMemo } from 'react';

import { CircularProgress } from '@mui/material';

import ProtectedData, { getSeverity, isProtectedData, UNKNOWN } from 'pages/components/ProtectedData';
import average from 'utils/average';
import toMeaningfulFixed from 'utils/to-meaningful-fixed';

import useStyles from './styles';

interface Props {
    scale: number;
    precision: number;
    data: { score: number | string }[];
    className?: string;
}

const Diagram: React.FC<Props> = ({ data, scale, precision, className }) => {
    const { classes, cx } = useStyles();

    const isProtected = useMemo(() => data.some(({ score }) => isProtectedData(score)), [data]);

    const score = useMemo(() => {
        const scores = data.map(({ score }) => score);

        if (isProtected) {
            return scores.find((score) => isProtectedData(score)) as string;
        } else {
            return average(...(data.map(({ score }) => score) as number[]));
        }
    }, [data, isProtected]);

    const severity = getSeverity(score, scale);

    return (
        <div
            className={cx(
                classes.diagram,
                {
                    [classes._green]: severity === 'Low',
                    [classes._orange]: severity === 'Medium',
                    [classes._red]: severity === 'High',
                    [classes._gray]: severity === UNKNOWN
                },
                className
            )}
        >
            <CircularProgress
                size={160}
                thickness={2.5}
                variant="determinate"
                value={100}
                className={classes.diagramBackdrop}
            />
            <CircularProgress
                size={160}
                thickness={2.5}
                className={classes.diagramValue}
                variant="determinate"
                value={isProtected ? 50 : Math.floor((Number(score) / (10 * scale)) * 100)}
            />
            <div className={classes.diagramLabel}>
                <span className={classes.diagramLabelScore}>
                    <ProtectedData data={score}>
                        {({ isProtected, content }) =>
                            isProtected ? content : toMeaningfulFixed(Number(content), precision)
                        }
                    </ProtectedData>
                </span>
                <span className={classes.diagramLabelRisk}>
                    <ProtectedData data={score}>
                        {({ isProtected, content }) => (isProtected ? content : `${severity} risk`)}
                    </ProtectedData>
                </span>
            </div>
        </div>
    );
};

export default Diagram;
